/**
 * User-related utility functions for the TC Issue Management UI
 */

/**
 * Interface for project user data structure
 */
export interface IProjectUser {
  readonly tiduuid?: string; // Made optional to match API response
  readonly id?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
}

export const findProjectUser = (
  projectUsers: readonly (IProjectUser | null | undefined)[] | null | undefined,
  userKey: string | undefined
): IProjectUser | undefined => {
  if (!projectUsers || !Array.isArray(projectUsers)) {
    return undefined;
  }

  return projectUsers.find(
    (entry): entry is IProjectUser =>
      entry != null && (entry.tiduuid === userKey || entry.id === userKey)
  );
};

/**
 * Utility function to map tiduuid to user name from project users data
 * @param tiduuid - The tiduuid to look up
 * @param projectUsersData - Array of project user data
 * @returns The user's full name or 'Unknown User' if not found
 */
export const getUserNameByTiduuid = (
  tiduuid: string,
  projectUsersData?: readonly (IProjectUser | null | undefined)[] | null
): string => {
  const user = findProjectUser(projectUsersData, tiduuid);

  if (user && user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  return 'Unknown User';
};

/**
 * Utility function to build authenticated image URL for thumbnail images
 * @param imageUrl - The image URL to authenticate
 * @param accessToken - Optional access token for authentication
 * @returns Promise resolving to authenticated image URL or base64 data
 */
export const buildAuthImg = async (
  imageUrl: string,
  accessToken?: string
): Promise<string> => {
  try {
    if (!imageUrl) {
      return '';
    }

    // If no access token, return the original URL
    if (!accessToken) {
      return imageUrl;
    }

    // Create authenticated URL with token
    const url = new URL(imageUrl);
    if (accessToken) {
      url.searchParams.set('access_token', accessToken);
    }

    // Fetch the image and convert to base64 for better caching and security
    const response = await fetch(url.toString(), {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- HTTP header names are case-insensitive but conventionally use this format
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error building authenticated image:', error);
    return '';
  }
};

/**
 * Utility function to extract and resolve user name from assignee string
 * @param assignee - The assignee string in format "users:tiduuid" or similar
 * @param projectUsersData - Array of project user data
 * @returns The user's full name or 'Unknown User' if not found
 */
export const getUserName = (
  assignee: string | undefined,
  projectUsersData?: readonly (IProjectUser | null | undefined)[] | null
): string => {
  if (!assignee) {
    return 'Unknown User';
  }

  // Extract tiduuid from "users:tiduuid" format
  const USERS_PREFIX = 'users:';
  const USERS_PREFIX_LENGTH = USERS_PREFIX.length;
  const tiduuid = assignee.startsWith(USERS_PREFIX)
    ? assignee.substring(USERS_PREFIX_LENGTH)
    : assignee.split(':')[1] || assignee;

  return getUserNameByTiduuid(tiduuid, projectUsersData);
};

export const sortLogic = (
  textA: string,
  textB: string,
  type: 'ASC' | 'DESC'
): number => {
  if (type === 'DESC') {
    return textA < textB ? -1 : textA > textB ? 1 : 0;
  } else {
    return textB < textA ? -1 : textB > textA ? 1 : 0;
  }
};
